import React, { createContext, useContext, useEffect, useReducer } from 'react'

import cookieHandler from './cookies'
import { AuthStatus, LoginDetails, LoginError, LoginRequest } from './types'

// In-app replacement for the auth part of @logtrade-technology-ab/logtrade-lib
// (LogtradeProvider/useLogtradeAuth). Same semantics: token lives in React state,
// is persisted in the shared `lt_token` cookie and restored from it on mount.

type AuthState = {
  status: AuthStatus
  token?: string
  error?: LoginError
}

type AuthAction =
  | { type: 'signIn.pending' }
  | { type: 'signIn.fulfilled'; payload: { token: string; remember: boolean } }
  | { type: 'signIn.rejected'; payload: LoginError }
  | { type: 'signOut' }
  | { type: 'setToken'; payload: string }

type AuthContextType = AuthState & {
  signIn: (details: LoginDetails, request: LoginRequest) => Promise<void>
  signOut: () => void
  setToken: (token: string) => void
}

const AUTH_INITIAL_STATE: AuthState = {
  status: AuthStatus.Idle,
}

const AuthContext = createContext<AuthContextType>(null as unknown as AuthContextType)

const authReducer: React.Reducer<AuthState, AuthAction> = (state, action) => {
  switch (action.type) {
    case 'signIn.pending':
      return { ...state, status: AuthStatus.Loading, error: undefined }
    case 'signIn.fulfilled':
      cookieHandler.saveToken(action.payload.token, action.payload.remember)
      return {
        ...state,
        token: action.payload.token,
        status: AuthStatus.Succeeded,
      }
    case 'signIn.rejected':
      return {
        ...state,
        token: undefined,
        status: AuthStatus.Failed,
        error: action.payload,
      }
    case 'signOut':
      cookieHandler.removeToken()
      return { ...state, token: undefined, status: AuthStatus.Idle }
    case 'setToken':
      return { ...state, token: action.payload }
    default:
      return state
  }
}

type AuthProviderProps = {
  children: React.ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [state, dispatch] = useReducer(authReducer, AUTH_INITIAL_STATE)

  const signIn = async (details: LoginDetails, request: LoginRequest) => {
    dispatch({ type: 'signIn.pending' })
    try {
      const response = await request(details)
      if (typeof response === 'string') {
        dispatch({
          type: 'signIn.fulfilled',
          payload: { token: response, remember: details.remember },
        })
      } else {
        dispatch({ type: 'signIn.rejected', payload: response })
      }
    } catch {
      dispatch({ type: 'signIn.rejected', payload: { code: 'Unknown' } })
    }
  }

  const signOut = () => dispatch({ type: 'signOut' })

  const setToken = (token: string) => dispatch({ type: 'setToken', payload: token })

  // Restore an existing session from the shared lt_token cookie on mount
  useEffect(() => {
    const token = cookieHandler.readToken()
    if (token) {
      dispatch({ type: 'setToken', payload: token })
    }
  }, [])

  return (
    <AuthContext.Provider value={{ ...state, signIn, signOut, setToken }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const auth = useContext(AuthContext)

  return {
    ...auth,
    isAuthenticated: auth.token !== undefined,
  }
}

export * from './types'
